import { BillingClaimStatusFilterSettings } from "@/Billing/Models/Settings/BillingClaimStatusFilterSettings";
import { BillingClaimStatusInboxModel } from "@/Billing/Models/UI/BillingClaimStatusInboxModel";
import { Client } from "@/Common/Api/Client";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { MedicalNoteModel } from "@/Patient/Api/Dto/Responses/MedicalNoteModel";
import { GetWorkSafeBcPhysicianReportFormResponse } from "@/Patient/Components/MedicalNote/WorkSafeBc/ResponseModels/GetWorkSafeBcPhysicianReportFormResponse";
import { BillingClaimActionRequest } from "./../Request/BillingClaimActionRequest";
import { BillingClaimNoteRequest } from "./../Request/BillingClaimNoteRequest";
import { BillingClaimStatusRequest } from "./../Request/BillingClaimStatusRequest";
import { CountBillingClaimStatusRequest } from "./../Request/CountBillingClaimStatusRequest";
import { BillingClaimNoteResponse } from "./BillingClaimNoteResponse";
import { BillingClaimStatusResponse } from "./BillingClaimStatusResponse";
import { CountBillingClaimStatusResponse } from "./CountBillingClaimStatusResponse";
import { ExternalBillingPatientIntervenorResponse } from "./ExternalBillingPatientIntervenorResponse";

async function fetchBillingClaimStatus(billingClaimStatusRequest: BillingClaimStatusRequest): Promise<BillingClaimStatusResponse> {
    // prevent backend from converting the date.
    // Filter are timezone agnostic and should be considered to be in the clinic's timezone no matter which timezone the user is working from.
    billingClaimStatusRequest.BillingClaimStatusFilterSettings.DateFrom = DateHelper.createDateAsUTC(billingClaimStatusRequest.BillingClaimStatusFilterSettings.DateFrom);
    billingClaimStatusRequest.BillingClaimStatusFilterSettings.DateTo = DateHelper.createDateAsUTC(billingClaimStatusRequest.BillingClaimStatusFilterSettings.DateTo);

    const response = await Client.postAsync<BillingClaimStatusResponse>(ServerOperations.Server.Myle_Billing_LoadClaimStatus, billingClaimStatusRequest);

    if (response.errors.any()) {
        sessionStore.showPopup(response.errors);
        return;
    }
    return response.data;
}

async function getBillingClaimStatusCount(request: CountBillingClaimStatusRequest): Promise<CountBillingClaimStatusResponse> {
    const response = await Client.postAsync<CountBillingClaimStatusResponse>(ServerOperations.Server.Myle_Billing_CountClaimStatus, request);
    return response.data;
}

async function billingClaimAction(billingClaimActionrequest: BillingClaimActionRequest): Promise<GenericResponse> {
    const response = await Client.postAsync<GenericResponse>(ServerOperations.Server.Myle_Billing_AddUpdateReconciliationAction, billingClaimActionrequest);
    return response.data;
}

async function saveBillingClaimNote(billingClaimNoteRequest: BillingClaimNoteRequest): Promise<BillingClaimNoteResponse> {
    const response = await Client.postAsync<BillingClaimNoteResponse>(ServerOperations.Server.Myle_Billing_AddReconciliationNote, billingClaimNoteRequest);
    if (response.errors.any()) {
        sessionStore.showPopup(response.errors);
        return;
    }
    return response.data;
}

async function saveBillingClaimStatusFilterSetting(filterSettings: BillingClaimStatusInboxModel): Promise<void> {
    const userSettings = new UserSettings();
    userSettings.BillingClaimStatusReportSettings = BillingClaimStatusInboxModel.toCacheModel({
        ...filterSettings,
        dateFrom: cacheStore.Helper.CreateDateAsUTC(filterSettings.dateFrom),
        dateTo: cacheStore.Helper.CreateDateAsUTC(filterSettings.dateTo)
    });

    const requestData = {
        UserSettings: userSettings,
        UserId: sessionStore.userId
    };

    const response = await Client.postAsync<BillingClaimStatusFilterSettings>(ServerOperations.Server.Myle_UpdateUserSettings, requestData);

    if (response.isSuccess)
        cacheStore.UserSettings.BillingClaimStatusReportSettings = userSettings.BillingClaimStatusReportSettings;
}

async function printBillingClaimReport(settings: BillingClaimStatusInboxModel, columnConfiguration: ColumnConfigurationModel[], searchText?: string): Promise<void> {
    const requestData = {
        BillingClaimStatusFilterSettings: settings,
        ColumnConfiguration: columnConfiguration,
        SearchText: searchText
    };

    const response = await Client.postAsync<string>(ServerOperations.Server.Myle_Billing_GenerateReconciliationPdf, requestData);

    if (response.errors.any()) {
        sessionStore.showPopup(response.errors);
        return;
    }

    PrintHelper.ShowPrintPreviewFileName(response.data["FileName"]);
}

async function fetchEncounterById(requestData: any): Promise<MedicalNoteModel> {
    const response = await Client.postAsync<any>(ServerOperations.Server.Myle_Patient_MedicalNote_LoadEncounter, requestData);
    const responseData: MedicalNoteModel = response.data;
    return responseData;
}

async function loadWorkSafeBc(requestData: any): Promise<any> {
    const response = await Client.postAsync<any>(ServerOperations.Server.WorkSafeBc_GetExistingFormList, requestData);
    return response.data;
}

async function loadPatient(requestData: any): Promise<Patient> {
    const response = await Client.postAsync<any>(ServerOperations.Server.Myle_LoadPatient, requestData);
    return Patient.FromResponseData(response.data);
}

async function getWorkSafeBcFormById(formId: string): Promise<GetWorkSafeBcPhysicianReportFormResponse> {
    const response = await Client.postAsync<GetWorkSafeBcPhysicianReportFormResponse>(
        ServerOperations.Server.WorkSafeBc_GetPhysicianReportForm,
        { Id: formId }
    );

    if (!response.isSuccess) {
        sessionStore.showPopup(response.errors);
        return;
    }

    return response.data;
}

async function getPrimaryClinicians(patientId: string): Promise<ExternalBillingPatientIntervenorResponse> {
    const response = await Client.postAsync<ExternalBillingPatientIntervenorResponse>(
        ServerOperations.Server.Myle_GetIntervenors,
        { PatientContactFacetId: patientId }
    );

    if (!response.isSuccess) {
        sessionStore.showPopup(response.errors);
        return;
    }

    return response.data;
}

export {
    billingClaimAction,
    fetchBillingClaimStatus,
    fetchEncounterById,
    getBillingClaimStatusCount,
    getPrimaryClinicians,
    getWorkSafeBcFormById,
    loadPatient,
    loadWorkSafeBc,
    printBillingClaimReport,
    saveBillingClaimNote,
    saveBillingClaimStatusFilterSetting
};
